import { useState } from 'react';
import type { Platform } from '@/types/software';
import { platformOptions, typeOptions, sortOptions, type SortValue } from '@/utils/catalog';

interface FilterBarProps {
  platforms: Platform[];
  onTogglePlatform: (p: Platform) => void;
  type: string;
  onTypeChange: (t: string) => void;
  updatedWithin: string;
  onUpdatedWithinChange: (v: string) => void;
  sort: SortValue;
  onSortChange: (s: SortValue) => void;
  onReset: () => void;
  showUpdatedWithin?: boolean;
  resultCount: number;
}

const updatedOptions = [
  { value: '', label: '全部时间' },
  { value: '7', label: '7天内' },
  { value: '30', label: '30天内' },
  { value: '90', label: '90天内' },
];

const chipClass = (active: boolean) =>
  `cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-xs transition-colors ${
    active
      ? 'bg-primary-500 font-medium text-background-50'
      : 'bg-background-100 text-foreground-600 hover:bg-secondary-100'
  }`;

export default function FilterBar({
  platforms,
  onTogglePlatform,
  type,
  onTypeChange,
  updatedWithin,
  onUpdatedWithinChange,
  sort,
  onSortChange,
  onReset,
  showUpdatedWithin = true,
  resultCount,
}: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const panel = (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <span className="w-20 shrink-0 text-sm font-medium text-foreground-700">系统平台</span>
        <div className="flex flex-wrap gap-2">
          {platformOptions.map((p) => (
            <button key={p} type="button" onClick={() => onTogglePlatform(p)} className={chipClass(platforms.includes(p))}>
              {p}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <span className="w-20 shrink-0 text-sm font-medium text-foreground-700">软件类型</span>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => onTypeChange('')} className={chipClass(type === '')}>
            不限
          </button>
          {typeOptions.map((t) => (
            <button key={t} type="button" onClick={() => onTypeChange(t)} className={chipClass(type === t)}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {showUpdatedWithin && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <span className="w-20 shrink-0 text-sm font-medium text-foreground-700">更新时间</span>
          <div className="flex flex-wrap gap-2">
            {updatedOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => onUpdatedWithinChange(opt.value)}
                className={chipClass(updatedWithin === opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <span className="w-20 shrink-0 text-sm font-medium text-foreground-700">排序方式</span>
        <div className="flex flex-wrap gap-2">
          {sortOptions.map((opt) => (
            <button key={opt.value} type="button" onClick={() => onSortChange(opt.value)} className={chipClass(sort === opt.value)}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="mb-4 hidden items-center justify-between gap-3 md:flex">
        <div className="flex items-center gap-2">
          <i className="ri-equalizer-line text-lg text-primary-600"></i>
          <span className="text-sm font-semibold text-foreground-900">筛选与排序</span>
          <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-800">共 {resultCount} 款</span>
        </div>
        <button
          type="button"
          onClick={onReset}
          className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-600"
        >
          <i className="ri-refresh-line"></i>重置筛选
        </button>
      </div>

      <div className="hidden md:block">{panel}</div>

      <div className="md:hidden">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex w-full cursor-pointer items-center justify-between rounded-md border border-background-300 bg-background-100 px-4 py-3"
        >
          <span className="flex items-center gap-2 text-sm font-medium text-foreground-800">
            <i className="ri-equalizer-line text-primary-600"></i>筛选与排序
          </span>
          <span className="flex items-center gap-2 text-xs text-foreground-400">
            共 {resultCount} 款
            <i className="ri-arrow-right-s-line text-base"></i>
          </span>
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-background-50 md:hidden">
          <div className="flex items-center justify-between border-b border-background-200 px-4 py-4">
            <span className="flex items-center gap-2 text-base font-semibold text-foreground-900">
              <i className="ri-equalizer-line text-primary-600"></i>筛选与排序
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="关闭筛选"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-5">{panel}</div>
          <div className="flex gap-3 border-t border-background-200 px-4 py-4">
            <button
              type="button"
              onClick={onReset}
              className="flex-1 cursor-pointer whitespace-nowrap rounded-md border border-background-300 py-3 text-sm font-medium text-foreground-700"
            >
              重置
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="flex-1 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 py-3 text-sm font-semibold text-background-50"
            >
              查看 {resultCount} 款结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}